const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;
const daysOfMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;
const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

type DateObjectDuration = {
  days?: number;
  months?: number;
  years?: number;
};

export class DateObject {
  datetimeValue: Date;

  constructor(val?: Date) {
    this.datetimeValue = val
      ? new Date(val.getFullYear(), val.getMonth(), val.getDate())
      : DateObject.today().datetimeValue;
  }

  // Operators
  public static equals(date1: DateObject, date2: DateObject): boolean {
    return (
      date1.year === date2.year &&
      date1.month === date2.month &&
      date1.date === date2.date
    );
  }

  public static equalsWeek(
    date1: DateObject,
    date2: DateObject,
    day: number = 0
  ): boolean {
    return this.equals(date1.toWeekDay(day), date2.toWeekDay(day));
  }

  public static lessThan(date1: DateObject, date2: DateObject): boolean {
    return date1.datetimeValue.getTime() < date2.datetimeValue.getTime();
  }

  public static greaterThan(date1: DateObject, date2: DateObject): boolean {
    return date1.datetimeValue.getTime() > date2.datetimeValue.getTime();
  }

  public static lessThanOrEqual(date1: DateObject, date2: DateObject): boolean {
    return date1.datetimeValue.getTime() <= date2.datetimeValue.getTime();
  }

  public static greaterThanOrEqual(
    date1: DateObject,
    date2: DateObject
  ): boolean {
    return date1.datetimeValue.getTime() >= date2.datetimeValue.getTime();
  }

  // Math
  public static add(
    dateObject: DateObject,
    duration: DateObjectDuration
  ): DateObject {
    return new DateObject(
      new Date(
        dateObject.datetimeValue.getFullYear() + (duration.years ?? 0),
        dateObject.datetimeValue.getMonth() + (duration.months ?? 0),
        dateObject.datetimeValue.getDate() + (duration.days ?? 0)
      )
    );
  }

  // Creators
  public duplicate(): DateObject {
    return new DateObject(this.datetimeValue);
  }

  public static today(): DateObject {
    return new DateObject(new Date());
  }

  public static fromString(str: string): DateObject | null {
    const temp = str.split("-");
    const day = parseInt(temp[2]);
    const month = parseInt(temp[1]);
    const year = parseInt(temp[0]);
    let valid = temp.length === 3;
    valid = valid && !isNaN(year);
    valid = valid && !isNaN(month);
    valid = valid && !isNaN(day);
    valid = valid && year > 0;
    valid = valid && month > 0;
    valid = valid && day > 0;
    valid = valid && year < 10000;
    valid = valid && month < 13;
    valid = valid && day <= daysOfMonth[parseInt(temp[1]) - 1];
    if (!valid) {
      return null;
    } else {
      return new DateObject(new Date(year, month - 1, day));
    }
  }

  // toString
  public toString(): string {
    return `${this.datetimeValue.getFullYear().toString()}-${(
      this.datetimeValue.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}-${this.datetimeValue
      .getDate()
      .toString()
      .padStart(2, "0")}`;
  }

  // toJSON
  public toJSON(): string {
    return this.toString();
  }

  // Utils
  public toWeekDay(day: number = 0): DateObject {
    const currentDay = this.datetimeValue.getDay();
    if (day <= currentDay) {
      return DateObject.add(this, { days: (day - currentDay) % 7 });
    } else {
      return DateObject.add(this, { days: (day - currentDay - 7) % 7 });
    }
  }

  public static daysBetween(date1: DateObject, date2: DateObject): number {
    const diffTime = Math.abs(
      date2.datetimeValue.getTime() - date1.datetimeValue.getTime()
    );
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  // Formats
  toDayDateMonth() {
    return (
      this.toDay() + " " + this.toDate() + "-" + (this.month + 1).toString()
    );
  }

  toMonthYear() {
    return this.toMonth() + " " + this.toYear();
  }

  toDayDateMonthYear() {
    return (
      this.toDay() +
      " " +
      this.toDate() +
      " " +
      this.toMonth() +
      " " +
      this.toYear()
    );
  }

  toDateMonthYear() {
    return this.date + " " + this.toMonth() + " " + this.year;
  }

  toDay() {
    return dayNames[this.day];
  }
  toDate() {
    return this.date.toString();
  }

  toMonth() {
    return monthNames[this.month];
  }

  toYear() {
    return this.year.toString();
  }

  // Getters
  get year(): number {
    return this.datetimeValue.getFullYear();
  }

  get month(): number {
    return this.datetimeValue.getMonth();
  }

  get day(): number {
    return this.datetimeValue.getDay();
  }

  get date(): number {
    return this.datetimeValue.getDate();
  }
}
